import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Styles from './NewsAggregator.module.scss';
import newsPlaceholder from 'assets/svg/news-placeholder-image.svg';
import newsLogo from 'assets/svg/news-logo.svg';
import { NewsItem, NEWS_PAGE_PATH } from 'views/news/newsTypes';
import { fetchNewsBatch } from 'views/news/fetchNews';
import { useNewsRealtime } from 'views/news/useNewsRealtime';

const NEWS_FETCH_LIMIT = 24;

const NewsAggregator: React.FC = () => {
  const history = useHistory();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const goToNewsPage = () => history.push(NEWS_PAGE_PATH);
  const goToNewsDetail = (id: number) => history.push(`/news/${id}`);

  const loadNews = useCallback(() => {
    fetchNewsBatch(0, NEWS_FETCH_LIMIT)
      .then(({ news: list, unreadCount: count }) => {
        setNews(list);
        setUnreadCount(count);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadNews();
  }, [loadNews]);

  // Real-time: refetch when a news item is published (no toast).
  useNewsRealtime(loadNews);

  return (
  <div className={Styles.wrapper}>
    <div className={Styles.header}>
      <div
        className={Styles.headerLeft}
        onClick={goToNewsPage}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            goToNewsPage();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="View all news"
      >
        <img src={newsLogo} alt="" className={Styles.headerIcon} />
        <span>NEWS AGGREGATOR</span>
        {unreadCount > 0 && <span className={Styles.countBadge}>{unreadCount}</span>}
      </div>
      <button type="button" className={Styles.viewAll} onClick={goToNewsPage}>
        View All
      </button>
    </div>

    <div className={Styles.newsList}>
      <div className={`${Styles.newsScroll} customScrollBar`}>
        {news.map(item => (
          <div
            className={`${Styles.newsItem} ${item.isRead ? '' : Styles.unread}`}
            key={item.id}
            role="button"
            tabIndex={0}
            onClick={() => goToNewsDetail(item.id)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                goToNewsDetail(item.id);
              }
            }}
            aria-label={`Read: ${item.description}`}
          >
            <div className={Styles.thumbnail}>
              <img src={item.image || newsPlaceholder} alt="" />
            </div>
            <div className={Styles.newsRight}>
              <div className={Styles.newsBody}>
                <span className={Styles.label}>{item.label}</span>
                <p className={Styles.description}>{item.description}</p>
              </div>
              <span className={Styles.readMore}>Read More</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
  );
};

export default NewsAggregator;
